import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useRejectObservation } from '../hooks';
import type { StagingRow } from '../schema';

interface Props {
  open: boolean;
  row: StagingRow | null;
  onClose: () => void;
}

export function RejectDialog({ open, row, onClose }: Props) {
  const rejectMutation = useRejectObservation();
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) setReason('');
  }, [open, row]);

  const submitting = rejectMutation.isPending;
  const canSubmit = reason.trim().length > 0 && !submitting;

  const onSubmit = async () => {
    if (!row || !canSubmit) return;
    try {
      await rejectMutation.mutateAsync({
        id: row.id,
        payload: { rejectReason: reason.trim() },
      });
      onClose();
    } catch {
      // Toast shown by the mutation's onError.
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      maxWidth="sm"
      fullWidth
      aria-labelledby="reject-staging-title"
    >
      <DialogTitle id="reject-staging-title">Reject observation</DialogTitle>
      <DialogContent dividers>
        <p className="mb-3 text-[13px] text-foreground-muted">
          Rejected rows are kept as training data — nothing is published or
          deleted. Give a short reason (e.g. photo unreadable, wrong crop, price
          illegible).
        </p>
        <TextField
          label="Reason"
          multiline
          minRows={3}
          fullWidth
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ maxLength: 500 }}
          error={reason.length > 0 && reason.trim().length === 0}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onSubmit}
          disabled={!canSubmit}
        >
          {submitting ? 'Rejecting…' : 'Reject'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
